import {DocumentRead, DocumentReadWReferences, DocumentReadWRefs, getEmailMetadata} from "../../schemas";

//
// export type DocFieldType =
//     'title'
//     | 'body'
//     | 'author'
//     | 'source'
//     | 'summary'
//     | 'from_email'
//     | 'to_email'
//     | 'x_from'
//     | 'x_to'
//     | 'x_cc'
//     | 'x_bcc'
//     | 'file_name'
//     | 'file_type'
//     | 'type'
//     | 'full_or_body';

export type DocFieldType = Partial<keyof DocumentReadWReferences>;

export const stringSearchContactFields: Set<string> = new Set([
    // Usually only has e-mail
    'from_email',
    'to_email',

    // Usually has name and e-mail
    'x_from',
    'x_to',
    'x_cc',
    'x_bcc',
]);

export const stringSearchUniqueFields: Set<string> = new Set([
    'summary',
    'title',
    'body',
]);


export interface FilteringConfiguration {
    startingOptions?: Set<string>;
}


export interface ContactInfo {
    name: string;
    email: string;
}

export function getEntitiesFromDoc(doc: DocumentRead, docField: DocFieldType): ContactInfo[] {
    const contacts: ContactInfo[] = [];
    const entityField: string = doc[docField] as string;
    if (!entityField) {
        return contacts;
    }
    // Split the field into potential name and email segments
    const segments = entityField.split(',').map(segment => segment.trim());

    segments.forEach(segment => {
        // Regex for standard email addresses
        const emailRegex = /[\w.-]+@[\w.-]+\.[\w.-]+/g;

        // Try matching as an email
        if (emailRegex.test(segment)) {
            contacts.push({name: "", email: segment});
        } else {
            // No email match, treat it as a potential name
            contacts.push({name: segment, email: ""});
        }
    });

    return contacts;
}

export function getUniqueEntitiesFromDocs(docs: DocumentRead[], docField: DocFieldType): Set<ContactInfo> {
    const contactsMap: Map<string, ContactInfo> = new Map();

    for (const doc of docs) {
        const contacts = getEntitiesFromDoc(doc, docField);
        contacts.forEach(contact => {
            // Use email as a unique key, or combine name and email for more specificity
            const key = `${contact.name}-${contact.email}`;
            contactsMap.set(key, contact);
        });
    }

    // Convert the Map values to a Set
    return new Set<ContactInfo>(contactsMap.values());
}


export interface TableItemForGridFormat {
    // [key: string]: string | number | Date | Document | null;
    id: string,
    title?: string,
    from_email?: string,
    to_email?: string,
    x_from?: string,
    x_to?: string,
    x_cc?: string,
    x_bcc?: string,
    summary?: string,
    score?: number,
    body?: string,
    checked?: boolean,
    file_name?: string,

    // New fields
    participants?: Array<{name?: string}>,
    type?: string,
    file_created_date?: string,
    file_modified_date?: string,
    content_date_start?: string,
    content_date_end?: string,

    page_count?: number,
    word_count?: number,
    body_char_count?: number,
    codes: string[],

    [key: string]: string | number | Date | DocumentRead | null | undefined | boolean | number[] | string[] | Array<{name?: string}>;
}

export class DocumentConverter {
    static toTableItem(doc: DocumentReadWRefs): TableItemForGridFormat {
        const emailMeta = getEmailMetadata(doc);
        return {
            title: doc.title || emailMeta?.subject || doc.file_name,
            id: doc.id,
            from_email: emailMeta?.from_email,
            to_email: emailMeta?.to_email,
            x_from: emailMeta?.x_from,
            x_to: emailMeta?.x_to,
            x_cc: emailMeta?.x_cc,
            x_bcc: emailMeta?.x_bcc,
            summary: doc.summary,
            body: doc.body,
            file_name: doc.file_name,
            checked: false,
            // New fields
            participants: doc.participants,
            type: doc.type,
            file_created_date: doc.file_created_date,
            file_modified_date: doc.file_modified_date,
            content_date_start: doc.content_date_start,
            content_date_end: doc.content_date_end,
            page_count: doc.page_body_offsets?.length || undefined,
            word_count: doc.body ? doc.body.split(/\s+/).filter(Boolean).length : undefined,
            body_char_count: doc.body_char_count ?? undefined,
            codes: doc.applied_tag_associations?.filter((association) => association.is_positive).map((association) => association.tag_id) || [],
        };
    }
}

